import {useMutation, useQuery} from '@tanstack/react-query';

import {
  RQ_ACCOUNTANT_LOOKUP_ALL,
  RQ_ACCOUNTANT_REGISTER,
  RQ_ACCOUNTANT_UPDATE,
  RQ_ADMIN_FILTER,
  RQ_ADMIN_LOOKUP_ALL,
  RQ_ADMIN_LOOKUP_ALL_ROLE,

  RQ_ADMIN_REGISTER,
  RQ_ADMIN_REGISTER_ROLE,
  RQ_ADMIN_UPDATE_ROLE,
  RQ_DELETE_USER,
  RQ_EMPLOYEE_FORGOT_PASSWORD,
  RQ_EMPLOYEE_LOOKUP_ALL,

  RQ_EMPLOYEE_LOOKUP_ALL_ACTIVE,
  RQ_EMPLOYEE_LOOKUP_ID,
  RQ_EMPLOYEE_LOOKUP_STATUS,
  RQ_EMPLOYEE_REGISTER,
  RQ_EMPLOYEE_RESET_PASSWORD,
  RQ_EMPLOYEE_UPDATE_ALL_FIELDS,
  RQ_SUPERVISOR_LOOKUP_ALL,
  RQ_SUPERVISOR_REGISTER,
  RQ_SUPERVISOR_UPDATE,

  RQ_USER_LOGIN,
} from './constants';
import {rqCreateRequest} from './rqHttpUtils';

const withUrl = (config, key, value) => ({
  ...config,
  url: config.url.replace(`<%= ${key} %>`, value),
});

export const RqEmployeeRegister = callbacks =>
  useMutation({
    mutationKey: ['rq_employee_register'],
    mutationFn: employeeData =>
      rqCreateRequest(RQ_EMPLOYEE_REGISTER, employeeData, null, null),
    ...callbacks,
  });

export const RqUserLogin = callbacks =>
  useMutation({
    mutationKey: ['rq_user_login'],
    mutationFn: loginData =>
      rqCreateRequest(RQ_USER_LOGIN, loginData, null, null),
    ...callbacks,
  });

export const RqSupervisorRegister = callbacks =>
  useMutation({
    mutationKey: ['rq_supervisor_register'],
    mutationFn: ({role, data, token}) =>
      rqCreateRequest(
        withUrl(RQ_SUPERVISOR_REGISTER, 'role', role),
        data,
        token,
        null,
      ),
    ...callbacks,
  });

export const RqAdminRegister = callbacks =>
  useMutation({
    mutationKey: ['rq_admin_register'],
    mutationFn: ({data, token}) =>
      rqCreateRequest(RQ_ADMIN_REGISTER, data, token, null),
    ...callbacks,
  });

export const RqAdminRegisterRole = callbacks =>
  useMutation({
    mutationKey: ['rq_admin_register_role'],
    mutationFn: ({role, data, token}) =>
      rqCreateRequest(
        withUrl(RQ_ADMIN_REGISTER_ROLE, 'role', role),
        data,
        token,
        null,
      ),
    ...callbacks,
  });

export const RqAccountantRegister = callbacks =>
  useMutation({
    mutationKey: ['rq_accountant_register'],
    mutationFn: ({role, data, token}) =>
      rqCreateRequest(
        withUrl(RQ_ACCOUNTANT_REGISTER, 'role', role),
        data,
        token,
        null,
      ),
    ...callbacks,
  });

export const RqSupervisorUpdate = callbacks =>
  useMutation({
    mutationKey: ['rq_supervisor_update'],
    mutationFn: ({id, data, token}) =>
      rqCreateRequest(
        withUrl(RQ_SUPERVISOR_UPDATE, 'id', id),
        data,
        token,
        null,
      ),
    ...callbacks,
  });

export const RqEmployeeUpdateAllFields = callbacks =>
  useMutation({
    mutationKey: ['rq_employee_update_all_fields'],
    mutationFn: ({id, data, token}) =>
      rqCreateRequest(
        withUrl(RQ_EMPLOYEE_UPDATE_ALL_FIELDS, 'id', id),
        data,
        token,
        null,
      ),
    ...callbacks,
  });

export const RqEmployeeResetPassword = callbacks =>
  useMutation({
    mutationKey: ['rq_employee_reset_password'],
    mutationFn: data =>
      rqCreateRequest(
        RQ_EMPLOYEE_RESET_PASSWORD,
        data,
        null,
        null,
      ),
    ...callbacks,
  });

export const RqEmployeeForgotPassword = callbacks =>
  useMutation({
    mutationKey: ['rq_employee_forgot_password'],
    mutationFn: data =>
      rqCreateRequest(
        RQ_EMPLOYEE_FORGOT_PASSWORD,
        data,
        null,
        null,
      ),
    ...callbacks,
  });

export const RqAdminUpdateRole = callbacks =>
  useMutation({
    mutationKey: ['rq_admin_update_role'],
    mutationFn: ({role, id, data, token}) =>
      rqCreateRequest(
        withUrl(
          withUrl(RQ_ADMIN_UPDATE_ROLE, 'role', role),
          'id',
          id,
        ),
        data,
        token,
        null,
      ),
    ...callbacks,
  });

export const RqAccountantUpdate = callbacks =>
  useMutation({
    mutationKey: ['rq_accountant_update'],
    mutationFn: ({id, data, token}) =>
      rqCreateRequest(
        withUrl(RQ_ACCOUNTANT_UPDATE, 'id', id),
        data,
        token,
        null,
      ),
    ...callbacks,
  });

export const RqDeleteUser = callbacks =>
  useMutation({
    mutationKey: ['rq_delete_user'],
    mutationFn: ({id, token}) =>
      rqCreateRequest(
        withUrl(RQ_DELETE_USER, 'id', id),
        null,
        token,
        null,
      ),
    ...callbacks,
  });

export const RqSupervisorLookupAll = (token, options = {}) =>
  useQuery({
    queryKey: ['rq_supervisor_lookup_all'],
    queryFn: () =>
      rqCreateRequest(
        RQ_SUPERVISOR_LOOKUP_ALL,
        null,
        token,
        null,
      ).then(res => res ?? []),
    enabled: !!token,
    ...options,
  });

export const RqEmployeeLookupById = (
  id,
  token,
  options = {},
) =>
  useQuery({
    queryKey: ['rq_employee_lookup_id', id],
    queryFn: () =>
      rqCreateRequest(
        withUrl(RQ_EMPLOYEE_LOOKUP_ID, 'id', id),
        null,
        token,
        null,
      ).then(res => res ?? null),
    enabled: !!id && !!token,
    ...options,
  });

export const RqEmployeeLookupStatus = (
  token,
  options = {},
) =>
  useQuery({
    queryKey: ['rq_employee_lookup_status'],
    queryFn: () =>
      rqCreateRequest(
        RQ_EMPLOYEE_LOOKUP_STATUS,
        null,
        token,
        null,
      ).then(res => res ?? []),
    enabled: !!token,
    ...options,
  });

export const RqEmployeeLookupAll = (
  token,
  options = {},
) =>
  useQuery({
    queryKey: ['rq_employee_lookup_all'],
    queryFn: () =>
      rqCreateRequest(
        RQ_EMPLOYEE_LOOKUP_ALL,
        null,
        token,
        null,
      ).then(res => res ?? []),
    enabled: !!token,
    ...options,
  });

export const RqEmployeeLookupAllActive = (
  token,
  options = {},
) =>
  useQuery({
    queryKey: ['rq_employee_lookup_all_active'],
    queryFn: () =>
      rqCreateRequest(
        RQ_EMPLOYEE_LOOKUP_ALL_ACTIVE,
        null,
        token,
        null,
      ).then(res => res ?? []),
    enabled: !!token,
    ...options,
  });

export const RqAdminLookupAll = (
  token,
  options = {},
) =>
  useQuery({
    queryKey: ['rq_admin_lookup_all'],
    queryFn: () =>
      rqCreateRequest(
        RQ_ADMIN_LOOKUP_ALL,
        null,
        token,
        null,
      ).then(res => res ?? []),
    enabled: !!token,
    ...options,
  });

export const RqAdminLookupAllRole = (
  role,
  token,
  options = {},
) =>
  useQuery({
    queryKey: ['rq_admin_lookup_all_role', role],
    queryFn: () =>
      rqCreateRequest(
        withUrl(RQ_ADMIN_LOOKUP_ALL_ROLE, 'role', role),
        null,
        token,
        null,
      ).then(res => res ?? []),
    enabled: !!role && !!token,
    ...options,
  });

export const RqAdminFilter = (
  token,
  options = {},
) =>
  useQuery({
    queryKey: ['rq_admin_filter'],
    queryFn: () =>
      rqCreateRequest(
        RQ_ADMIN_FILTER,
        null,
        token,
        null,
      ).then(res => res ?? []),
    enabled: !!token,
    ...options,
  });

export const RqAccountantLookupAll = (
  token,
  options = {},
) =>
  useQuery({
    queryKey: ['rq_accountant_lookup_all'],
    queryFn: () =>
      rqCreateRequest(
        RQ_ACCOUNTANT_LOOKUP_ALL,
        null,
        token,
        null,
      ).then(res => res ?? []),
    enabled: !!token,
    ...options,
  });
